// src/services/customer/MitarbeiterSichtbarkeitService.ts
//
// Freigabe der Mitarbeiter-Sichtbarkeit für den Lohnschein-Versand.
//
// Gelesen wird ausschließlich aus der Datenbanksicht
// mitarbeiter_sichtbarkeit_vorschlag_v. Sie verbindet den DATEV-Stand mit der
// bestätigten Klassifizierung und berechnet Status und Vorschlag. Der
// Fingerabdruck-Abgleich lebt damit an genau einer Stelle, nämlich in der
// Datenbank, und nicht zusätzlich hier.
//
// Geschrieben wird in mitarbeiter_sichtbarkeit. Diese Tabelle hält nur
// bestätigte Entscheidungen; ein Vorschlag wird niemals gespeichert.

import { supabase } from '../../shared/services/supabase';
import {
  MitarbeiterSichtbarkeitZeile,
  MitarbeiterSichtbarkeitKlasse
} from '../../shared/types';

export interface FreigabeEingabe {
  customerId: string | null;
  beraternummer: string;
  mandantennummer: string;
  personalnummer: string;
  klasse: MitarbeiterSichtbarkeitKlasse;
  betrieblichePersonalnummer: string | null;
  kostenstelle: string | null;
  taetigkeit: string | null;
  bestaetigtVon: string;
  quelle: 'kunde' | 'xtax';
  notiz?: string | null;
}

/**
 * Sortierung nach Personalnummer statt nach Nachname.
 *
 * Grund: Beim Zuordnen intern/extern ist die Personalnummer das eigentliche
 * Pruefmerkmal. Die internen Nummernkreise (unter 1000, 5700 bis 5799, ab
 * 90000) liegen dann zusammenhaengend beieinander und ein falsch eingestufter
 * Eintrag faellt sofort auf. Nach Nachname sortiert verteilen sie sich ueber
 * die ganze Liste.
 *
 * Numerisch, nicht alphabetisch: sonst stuende 100 vor 99. Nicht-numerische
 * Personalnummern wandern ans Ende und werden untereinander alphabetisch
 * sortiert.
 */
function vergleicheNachPersonalnummer(
  a: MitarbeiterSichtbarkeitZeile,
  b: MitarbeiterSichtbarkeitZeile
): number {
  const roh = (z: MitarbeiterSichtbarkeitZeile) => (z.personalnummer ?? '').trim();
  const zahl = (wert: string) => (/^\d+$/.test(wert) ? Number(wert) : null);

  const za = zahl(roh(a));
  const zb = zahl(roh(b));

  if (za !== null && zb !== null) return za - zb;
  if (za !== null) return -1;
  if (zb !== null) return 1;
  return roh(a).localeCompare(roh(b), 'de');
}

class MitarbeiterSichtbarkeitService {
  /**
   * Lädt alle Mitarbeiter eines Mandanten mit Vorschlag und Status.
   * `nurAktive` blendet ausgetretene Mitarbeiter aus.
   */
  async getZeilen(
    beraternummer: string,
    mandantennummer: string,
    nurAktive: boolean = true
  ): Promise<MitarbeiterSichtbarkeitZeile[]> {
    try {
      let query = supabase
        .from('mitarbeiter_sichtbarkeit_vorschlag_v')
        .select('*')
        .eq('beraternummer', beraternummer)
        .eq('mandantennummer', mandantennummer)
        .limit(100000);

      if (nurAktive) {
        query = query.eq('ist_aktiv', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading Sichtbarkeit rows:', error);
        return [];
      }

      const zeilen = (data ?? []) as MitarbeiterSichtbarkeitZeile[];
      return zeilen.sort(vergleicheNachPersonalnummer);
    } catch (err) {
      console.error('Unexpected error loading Sichtbarkeit rows:', err);
      return [];
    }
  }

  /**
   * Lädt die Mitarbeiter, für die eine Zuordnung tatsächlich gebraucht wird:
   * alle aktiven plus alle, die in den letzten Abrechnungsperioden ein Dokument
   * haben, unabhängig vom Austritt.
   *
   * Warum nicht einfach nur die aktiven: Der Versand betrifft naturgemäß auch
   * Ausgetretene, ihre letzte Abrechnung gehört zum Monat. Am 11.09.2026 sind
   * Dessau, München und Berlin am harten Block gescheitert, weil 75 ausgetretene
   * Mitarbeiter mit August-Dokument im Panel gar nicht sichtbar waren und sich
   * deshalb nicht zuordnen liessen.
   *
   * Warum nicht einfach alle: Der Spiegel enthält den kompletten Bestand über
   * Jahre, bei einzelnen Standorten über tausend Personen. Eine Liste, in der
   * die relevanten Fälle untergehen, hilft niemandem.
   *
   * @param monateZurueck Wie viele Abrechnungsperioden rückwärts betrachtet
   *   werden. Drei deckt den laufenden Monat und zwei Vormonate ab und damit
   *   jeden Fall, der über eine Nachzügler-Kachel noch versandt werden kann.
   */
  async getZeilenMitVersandbedarf(
    beraternummer: string,
    mandantennummer: string,
    customerId: string | null,
    monateZurueck: number = 3
  ): Promise<MitarbeiterSichtbarkeitZeile[]> {
    try {
      const { data, error } = await supabase
        .from('mitarbeiter_sichtbarkeit_vorschlag_v')
        .select('*')
        .eq('beraternummer', beraternummer)
        .eq('mandantennummer', mandantennummer)
        .limit(100000);

      if (error) {
        console.error('Error loading Sichtbarkeit rows:', error);
        return [];
      }
      const alle = (data ?? []) as MitarbeiterSichtbarkeitZeile[];

      // Personalnummern mit Dokumenten in den betrachteten Perioden.
      const mitDokument = new Set<string>();
      if (customerId) {
        const jetzt = new Date();
        const ab = new Date(jetzt.getFullYear(), jetzt.getMonth() - (monateZurueck - 1), 1);
        const abStr = `${ab.getFullYear()}-${String(ab.getMonth() + 1).padStart(2, '0')}-01`;

        const { data: dokumente, error: dokErr } = await supabase
          .from('datev_documents')
          .select('employee_number')
          .eq('customer_id', customerId)
          .gte('period', abStr)
          .in('document_type', ['LOBN', 'LSTB', 'SVNW'])
          .not('employee_number', 'is', null)
          .limit(100000);

        if (dokErr) {
          // Nicht blockierend: ohne Dokumentenliste bleibt es bei den aktiven.
          console.warn('Error loading document employee numbers:', dokErr.message);
        } else {
          (dokumente ?? []).forEach((d: { employee_number: number }) => {
            mitDokument.add(String(d.employee_number).trim());
          });
        }
      }

      const relevant = alle.filter(
        z => z.ist_aktiv || mitDokument.has((z.personalnummer ?? '').trim())
      );

      return relevant.sort(vergleicheNachPersonalnummer);
    } catch (err) {
      console.error('Unexpected error loading Sichtbarkeit rows:', err);
      return [];
    }
  }

  /**
   * Setzt oder aktualisiert die Freigabe für einen Mitarbeiter. Die drei
   * Stammdatenwerte werden mitgeschrieben; die Datenbank bildet daraus den
   * Fingerabdruck. Ändert sich später einer dieser Werte, verfällt die
   * Freigabe automatisch.
   */
  async setKlasse(eingabe: FreigabeEingabe): Promise<boolean> {
    return this.setKlasseMehrfach([eingabe]);
  }

  /**
   * Setzt mehrere Freigaben in einem Zug. Wird für die Massenübernahme der
   * Vorschläge verwendet.
   */
  async setKlasseMehrfach(eingaben: FreigabeEingabe[]): Promise<boolean> {
    if (eingaben.length === 0) return true;

    try {
      const jetzt = new Date().toISOString();
      const zeilen = eingaben.map(e => ({
        customer_id: e.customerId,
        beraternummer: e.beraternummer,
        mandantennummer: e.mandantennummer,
        personalnummer: e.personalnummer,
        klasse: e.klasse,
        fp_betriebliche_personalnummer: e.betrieblichePersonalnummer,
        fp_kostenstelle: e.kostenstelle,
        fp_taetigkeit: e.taetigkeit,
        bestaetigt_von: e.bestaetigtVon,
        bestaetigt_am: jetzt,
        quelle: e.quelle,
        notiz: e.notiz ?? null,
        updated_at: jetzt
      }));

      const { error } = await supabase
        .from('mitarbeiter_sichtbarkeit')
        .upsert(zeilen, { onConflict: 'beraternummer,mandantennummer,personalnummer' });

      if (error) {
        console.error('Error saving Sichtbarkeit:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Unexpected error saving Sichtbarkeit:', err);
      return false;
    }
  }

  /**
   * Nimmt eine Freigabe zurück. Der Mitarbeiter fällt damit auf den Status
   * offen und wird nur noch an Kontakte mit Vollzugriff ausgeliefert.
   */
  async entferneFreigabe(
    beraternummer: string,
    mandantennummer: string,
    personalnummer: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('mitarbeiter_sichtbarkeit')
        .delete()
        .eq('beraternummer', beraternummer)
        .eq('mandantennummer', mandantennummer)
        .eq('personalnummer', personalnummer);

      if (error) {
        console.error('Error removing Sichtbarkeit:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Unexpected error removing Sichtbarkeit:', err);
      return false;
    }
  }

  /**
   * Liest den Sichtbarkeits-Schalter des Kunden.
   */
  async getSchalter(customerId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('lohnschein_sichtbarkeit_aktiv')
        .eq('id', customerId)
        .maybeSingle();

      if (error) {
        console.error('Error loading Sichtbarkeit switch:', error);
        return false;
      }
      return data?.lohnschein_sichtbarkeit_aktiv === true;
    } catch (err) {
      console.error('Unexpected error loading Sichtbarkeit switch:', err);
      return false;
    }
  }

  /**
   * Setzt den Sichtbarkeits-Schalter des Kunden.
   */
  async setSchalter(customerId: string, aktiv: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customers')
        .update({ lohnschein_sichtbarkeit_aktiv: aktiv, updated_at: new Date().toISOString() })
        .eq('id', customerId);

      if (error) {
        console.error('Error saving Sichtbarkeit switch:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Unexpected error saving Sichtbarkeit switch:', err);
      return false;
    }
  }
}

export const mitarbeiterSichtbarkeitService = new MitarbeiterSichtbarkeitService();
